import { Box, Typography, Tooltip } from "@mui/material";
import {
  BADGE_CONFIG,
  BADGE_SORT_ORDER,
  DEFAULT_BADGE_COLOR,
} from "common/badgeConfig";

interface UserBadgeProps {
  badges: Record<string, number>;
  sort?: "count" | "standard";
  showCount?: boolean;
  showTooltip?: boolean;
  variant?: "default" | "inline";
  maxBadges?: number;
  profileUrl?: string;
}

export default ({
  badges,
  sort = "standard",
  showCount,
  showTooltip = true,
  variant = "default",
  maxBadges,
  profileUrl,
}: UserBadgeProps) => {
  const isInline = variant === "inline";
  const shouldShowCount = showCount ?? false;
  const badgeLimit = maxBadges ?? (isInline ? 2 : undefined);
  if (!badges || Object.keys(badges).length === 0) {
    return null;
  }

  // Get all badges the user has
  let userBadges = Object.entries(badges)
    .filter(([_, count]) => count > 0)
    .map(([badgeName, count]) => ({ name: badgeName, count }));

  if (userBadges.length === 0) {
    return null;
  }

  // Sort badges
  if (sort === "count") {
    userBadges.sort((a, b) => b.count - a.count);
  } else {
    // Standard sort - use predefined order
    userBadges.sort((a, b) => {
      const aIndex = BADGE_SORT_ORDER.indexOf(a.name);
      const bIndex = BADGE_SORT_ORDER.indexOf(b.name);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }

  const iconSize = isInline ? 16 : 20;
  const displayedBadges = badgeLimit
    ? userBadges.slice(0, badgeLimit)
    : userBadges;
  const hasMore = badgeLimit && userBadges.length > badgeLimit;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: isInline ? "4px" : "4px",
      }}
    >
      {displayedBadges.map(({ name, count }) => {
        const config = BADGE_CONFIG[name];
        if (!config) return null;

        const BadgeIcon = config.icon;
        const badgeColor =
          config.color === DEFAULT_BADGE_COLOR ? undefined : config.color;

        const badgeElement = (
          <Box
            key={name}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1px",
            }}
          >
            <Box
              sx={(theme) => ({
                color: badgeColor || theme.palette.text.secondary,
                display: "flex",
                alignItems: "center",
              })}
            >
              <BadgeIcon size={iconSize} />
            </Box>
            {shouldShowCount && (
              <Typography
                sx={(theme) => ({
                  color: badgeColor || theme.palette.text.secondary,
                  fontSize: isInline ? "13px" : "14px",
                  fontWeight: 600,
                  lineHeight: 1,
                })}
              >
                {count}
              </Typography>
            )}
          </Box>
        );

        if (!showTooltip) {
          return badgeElement;
        }

        const tooltipContent = (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              padding: "4px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Box
                sx={(theme) => ({
                  color: badgeColor || theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                })}
              >
                <BadgeIcon size={20} />
              </Box>
              <Typography variant="bodyHeavy">{config.label}</Typography>
            </Box>
            <Typography variant="bodySmall">{config.description}</Typography>
            {!config.isSingular && (
              <Typography variant="bodyHeavy">
                Earned {count} {count === 1 ? "time" : "times"}
              </Typography>
            )}
          </Box>
        );

        return (
          <Tooltip key={name} title={tooltipContent} arrow>
            {badgeElement}
          </Tooltip>
        );
      })}
      {hasMore && (
        <Typography
          component={"span"}
          {...(profileUrl && {
            to: profileUrl,
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
          })}
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            fontSize: isInline ? "13px" : "14px",
            fontWeight: 600,
            textDecoration: "none",
            cursor: profileUrl ? "pointer" : "default",
            "&:hover": profileUrl
              ? { color: theme.palette.text.primary }
              : undefined,
          })}
        >
          +{userBadges.length - badgeLimit!}
        </Typography>
      )}
    </Box>
  );
};
